import { MAX_REVERSE_VESTING_REPURCHASE_PRICE } from 'shared';
import { z } from 'zod';

import { SharePlanType } from '../../../../types/pool-plans.types';
import { TerminationCause } from '../../../../types/stakeholder.types';
import { formatDisplayValue } from '../../../../utils/format-display-value.util';
import { getTranslation } from '../../../../utils/get-translation.util';

export const terminateForm = z.object({
  stakeholderItems: z
    .array(
      z.object({
        id: z.string().trim().min(1),
        terminationCause: z.enum(TerminationCause),
        terminationDate: z.coerce.date(),
        lastVestingDate: z.coerce.date().optional(),
        /** Whether `lastVestingDate` was picked by the operator rather than derived from grants. */
        isLastVestingDateManual: z.boolean().optional(),
        grantItems: z
          .array(
            z.object({
              id: z.string().trim().min(1),
              grantItem: z.object({
                plan: z.object({ type: z.enum(SharePlanType) }),
                numbersOfGrants: z.coerce
                  .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  .optional(),
              }),
              vestedSharesCount: z.coerce
                .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                .optional(),
              exercisedCount: z.coerce
                .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                .optional(),
              finalExerciseDate: z.coerce.date().optional(),
              fullVestingCalculatedDate: z.coerce.date().optional(),
            }),
          )
          .optional(),
        reverseVestingItems: z
          .array(
            z
              .object({
                id: z.string().trim().min(1),
                /**
                 * Contractual figures are echoed into the form so the row can validate and total
                 * itself without re-reading the preview response — the backend recomputes them from
                 * the schedule on submit and never trusts these.
                 */
                contractualUnvestedShares: z.coerce.number(),
                contractualVestedShares: z.coerce.number(),
                reverseVestingShares: z.coerce.number(),
                acceleratedShares: z.coerce
                  .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  .int({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  .nonnegative({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  .optional(),
                repurchasePrice: z.coerce
                  .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  .nonnegative({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                  // The same ceiling `terminateReverseVestingItemSchema` re-applies, so a mistyped
                  // price is an inline field error rather than a backend 400 toast after submit.
                  .max(MAX_REVERSE_VESTING_REPURCHASE_PRICE, {
                    message: getTranslation('MAX_VALUE', {
                      value: formatDisplayValue(MAX_REVERSE_VESTING_REPURCHASE_PRICE, {
                        roundTo: 0,
                      }),
                    }),
                  })
                  .optional(),
              })
              // The acceleration ceiling the table paints inline, re-applied here because
              // handleSubmit re-runs the resolver — whose output replaces manual errors — so
              // without a schema-level rule an over-cap value would submit and come back as a
              // backend 400 toast instead of the inline field error. Mirrors
              // ReverseVestingTerminationService.resolveAcceleratedShares.
              .superRefine(({ acceleratedShares, contractualUnvestedShares }, ctx) => {
                if (acceleratedShares === undefined) return;
                if (acceleratedShares <= contractualUnvestedShares) return;

                ctx.addIssue({
                  code: 'custom',
                  // Same formatter as the table's inline effect, so the two sources of this
                  // error can never render different ceilings.
                  message: getTranslation('MAX_VALUE', {
                    value: formatDisplayValue(contractualUnvestedShares, { roundTo: 0 }),
                  }),
                  path: ['acceleratedShares'],
                });
              }),
          )
          .optional(),
      }),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
  isAgree: z.boolean().refine((value) => value === true, {
    message: 'isAgree must be true',
  }),
  isSendEmails: z.boolean().optional(),
});

export const formSchema = z.object({
  terminateForm,
});

export type FormSchema = z.infer<typeof formSchema>;
